import { PointOps } from '@/lib/bezieLib/Graphics/PointOps';
import { CopyUtils, NumPair, Point } from '../../../Misc/Utils';

export class VectorStruct{
    public static readonly START_CTRL_PT = 0;
    public static readonly END_CTRL_PT = 1;

    private vertices : Point[] = []; //Vertices are all relative to origin
    private controlPoints: [Point[]] = [[]]; //Array of 2 points. [ [Ctrl Pt A, Ctrl Pt B] , [Ctrl Pt C, Ctrl Pt D] ] where A, B are for vertex 0 and C, D are for vertex 1 etc
    private loop : boolean = false;
    private min : NumPair = [0,0]; //min x, min y
    private max : NumPair = [0,0]; //max x, max y

    public constructor(){
        this.controlPoints.pop(); //hack to prevent having the empty array as first value in controlPoints array.
    }

    public getMin(): Point{
        return [this.min[0], this.min[1]];
    }

    public getMax(): Point{
        return [this.max[0], this.max[1]];
    }

    public getVertices(): Point[]{
        return this.vertices;
    }

    public getVertex(index: number): Point{
        if(index < this.vertices.length){
            return [this.vertices[index][0], this.vertices[index][1]];
        }
        return [0,0];
    }

    public getFirstVertex(): Point{
        if(this.getVertexCount() > 0){
            return this.vertices[0];
        }
        return [0,0];
    }

    public getLastVertex(): Point{
        if(this.getVertexCount() > 0){
            return this.vertices[this.getVertexCount()-1];
        }
        return [0,0];
    }

    public getVertexCount(): number{
        return this.vertices.length;
    }

    public getControlPoints(): [Point[]]{
        return this.controlPoints;
    }

    public getControlPoint(index: number): Point[]{
        if(index < this.vertices.length){
            return [this.controlPoints[index][VectorStruct.START_CTRL_PT], this.controlPoints[index][VectorStruct.END_CTRL_PT]];
        }
        return [[0,0],[0,0]];
    }

    public isLoop() { return this.loop }

    public setLoop(shouldLoop: boolean){
        this.loop = shouldLoop;
    }

    /**
     * Adds a vertex and an entry in controlpoints
     * @param p the point to be added
     */
    public addVertex(p:Point){
        this.vertices.push(p);
        this.controlPoints.push([p,p]);
        console.log("added v. "+this.vertices)
        console.log(" cp:"+this.controlPoints);
        //console.log("ref: "+this.reference);
        this.evaluateMinMax(p);
    }

    /**
     * Removes last vertex by default
     */
    public removeVertex(){
        this.vertices.pop();
        this.controlPoints.pop();
        this.reComputeMinMax();
    }

    private evaluateMinMax(newPoint: Point){
        if(newPoint[0] < this.min[0]){
            this.min[0] = newPoint[0];
        }
        if(newPoint[1] < this.min[1]){
            this.min[1] = newPoint[1];
        }
        if(newPoint[0] > this.max[0]){
            this.max[0] = newPoint[0];
        }
        if(newPoint[1] > this.max[1]){
            this.max[1] = newPoint[1];
        }
    }

    //used only when all points need to be moved by 'change'. This is done when center changes due to vertex addition or vertex is moved by user.
    public translate(change: Point){
        for(var i = 0; i < this.vertices.length; i++){
            this.vertices[i] = PointOps.add(this.vertices[i], change);
            this.setControlPoint(i, 
                PointOps.add(this.controlPoints[i][0], change), 
                PointOps.add(this.controlPoints[i][1], change));
        }
        this.reComputeMinMax(); //added for BVector
    }

    public translateEntity(i: number, change: Point){
        if(i < this.getVertexCount()){
            this.vertices[i] = PointOps.add(this.vertices[i], change);
            this.setControlPoint(i, 
                PointOps.add(this.controlPoints[i][0], change), 
                PointOps.add(this.controlPoints[i][1], change));
            this.evaluateMinMax(this.vertices[i]);
        }
    }

    private reComputeMinMax(){
        this.min = this.getVertex(0);
        this.max = this.getVertex(0);
        for(var i = 0; i < this.getVertexCount(); i++){
            var pt = this.getVertex(i);
            if(pt[0] < this.min[0]){
                this.min[0] = pt[0];
            }else if(pt[0] > this.max[0]){
                this.max[0] = pt[0];
            }
            
            if(pt[1] < this.min[1]){
                this.min[1] = pt[1];
            }else if(pt[1] > this.max[1]){
                this.max[1] = pt[1];
            }
        }
    }

    //Returns the directional winding of the polygon/vertices.
    // 0 - clock wise; 1 - counter clockwise
    public getWinding(){
        var area = 0; //area under line segment
        if(this.getVertexCount() > 1){
            for(var i = 0; i < this.vertices.length-1; i++){
                area += this.areaUnderLineSegment(this.vertices[i], this.vertices[i+1])
            }
            area += this.areaUnderLineSegment(this.vertices[this.getVertexCount()-1], this.vertices[0])
        }
        if(area<0){
            return 1 //clockwise
        }else{
            return 0 //counter clockwise
        }
    }

    private areaUnderLineSegment(a: Point, b: Point){
        return (b[0]-a[0])*(b[1]+a[1]); //https://www.element84.com/blog/determining-the-winding-of-a-polygon-given-as-a-set-of-ordered-points
    }

    public clone(): VectorStruct{
        var clone = new VectorStruct()
        clone.vertices = Object.assign([], this.vertices);
        this.controlPoints.forEach(val => clone.controlPoints.push(Object.assign([], val)));
        clone.loop = this.loop;
        clone.max = Object.assign([], this.max);
        clone.min = Object.assign([], this.min);
        return clone;
    }

    /** AVOID USING
     * Sets the vertex and controlpoints
     * @param index index
     * @param p the point to be added
     */
     public setVertex(index: number, p:Point){
        if(index < this.vertices.length){
            this.vertices[index] = p;
            this.controlPoints[index] = [p,p];
            this.reComputeMinMax();
        }
    }

    /** AVOID USING
     * Sets only controlpoints
     * @param index index
     * @param p the point to be added
     */
     public setControlPoint(index: number, startControlPoint: Point, endControlPoint: Point){
        if(index < this.vertices.length){
            this.controlPoints[index] = [startControlPoint, endControlPoint];
        }
    }
}